<script setup lang="ts">
/**
 * Key figures: one to six metrics, each a quiet label over a large figure with
 * an optional unit, change (delta) and error line. A single metric is set as
 * the hero figure; more share the room and step the figure size down.
 *
 * `layout` `auto` arranges the metrics by the room the grid itself has (a
 * container query, not the viewport): one column when narrow, then two, then
 * up to four in a row. `row`, `grid2` and `stack` force one arrangement.
 *
 * The caller formats the figures (`value` is text). The `extra` slot adds
 * something per metric next to the figure — a badge, say.
 *
 * Not a card: it has no frame of its own and sits inside a panel or tile. For
 * a framed single tile use a card around it.
 */
import { computed } from 'vue';

export interface NxMetric {
  key: string | number;
  label: string;
  /** The formatted figure. */
  value: string;
  unit?: string;
  /** Colours the figure when it states something (overdue in `danger`). */
  tone?: 'neutral' | 'info' | 'success' | 'warning' | 'danger';
  /** Change against a named period: text („+12 % zum Vormonat“) and whether it is good. */
  delta?: { text: string; direction: 'up' | 'down' | 'flat'; good?: boolean };
  /** A muted line under the figure. */
  hint?: string;
  /** Replaces the figure's meaning: the metric could not be computed. */
  error?: string;
  /** Title of the figure (full, unrounded value). */
  title?: string;
}

const props = withDefaults(
  defineProps<{
    metrics: readonly NxMetric[];
    layout?: 'auto' | 'row' | 'grid2' | 'stack';
    /** Metrics shown at most. */
    max?: number;
  }>(),
  { layout: 'auto', max: 6 }
);

defineSlots<{
  extra?: (props: { metric: NxMetric; index: number }) => unknown;
}>();

const shown = computed(() => props.metrics.slice(0, props.max));
const count = computed(() => shown.value.length);

function deltaClass(m: NxMetric) {
  const d = m.delta;
  if (!d) return undefined;
  if (d.direction === 'flat' || d.good === undefined) return 'is-neutral';
  return d.good ? 'is-good' : 'is-bad';
}
const arrow = { up: '↑', down: '↓', flat: '→' } as const;
</script>

<template>
  <div class="nx-metric-grid" :style="{ '--cols': String(count) }">
    <dl class="nx-metric-grid__list" :data-layout="props.layout" :data-count="String(count)">
      <div
        v-for="(m, index) in shown"
        :key="m.key"
        class="nx-metric"
        :class="m.tone && m.tone !== 'neutral' ? `tone-${m.tone}` : undefined"
        :data-has-error="m.error ? 'true' : undefined"
        data-testid="nx-metric"
      >
        <dt class="nx-metric__label" :title="m.label">{{ m.label }}</dt>
        <dd class="nx-metric__body">
          <span class="nx-metric__figure-row">
            <span class="nx-metric__value" :title="m.title">{{ m.value }}</span>
            <span v-if="m.unit" class="nx-metric__unit">{{ m.unit }}</span>
            <slot name="extra" :metric="m" :index="index" />
          </span>
          <span v-if="m.delta" class="nx-metric__delta" :class="deltaClass(m)">
            <span aria-hidden="true">{{ arrow[m.delta.direction] }}</span>
            {{ m.delta.text }}
          </span>
          <span v-if="m.hint" class="nx-metric__hint">{{ m.hint }}</span>
          <span v-if="m.error" class="nx-metric__error" role="alert">{{ m.error }}</span>
        </dd>
      </div>
    </dl>
  </div>
</template>

<style scoped>
.nx-metric-grid {
  container: nx-metric-grid / inline-size;
  width: 100%;
}
.nx-metric-grid__list {
  --figure-size: calc(var(--nxt-size-unit) * 2.25);

  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--nxt-space-md) var(--nxt-space-lg);
  margin: 0;
}
.nx-metric-grid__list[data-count='2'],
.nx-metric-grid__list[data-count='3'] {
  --figure-size: calc(var(--nxt-size-unit) * 1.75);
}
.nx-metric-grid__list:is([data-count='4'], [data-count='5'], [data-count='6']) {
  --figure-size: calc(var(--nxt-size-unit) * 1.4);
}
.nx-metric-grid__list[data-layout='row'] {
  grid-template-columns: repeat(var(--cols), minmax(0, 1fr));
}
.nx-metric-grid__list[data-layout='grid2'] {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
@container nx-metric-grid (min-width: 220px) {
  .nx-metric-grid__list[data-layout='auto']:not([data-count='1']) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@container nx-metric-grid (min-width: 380px) {
  .nx-metric-grid__list[data-layout='auto']:is(
      [data-count='3'],
      [data-count='5'],
      [data-count='6']
    ) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .nx-metric-grid__list[data-layout='auto'][data-count='4'] {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.nx-metric {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}
.nx-metric-grid__list[data-layout='stack'] .nx-metric:not(:last-child) {
  padding-block-end: var(--nxt-space-md);
  border-block-end: var(--nxt-hairline) solid var(--nxt-border);
}
.nx-metric__label {
  overflow: hidden;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
  letter-spacing: 0.02em;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}
.nx-metric__body {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  margin: 0;
  min-width: 0;
}
.nx-metric__figure-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--nxt-space-2xs) var(--nxt-space-xs);
  min-width: 0;
}
.nx-metric__value {
  max-width: 100%;
  overflow: hidden;
  color: var(--metric-figure, var(--nxt-fg));
  font-size: var(--figure-size);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: -0.025em;
  line-height: 1.05;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-metric__unit {
  color: var(--nxt-fg-muted);
  font-size: calc(var(--figure-size) * 0.45);
  font-weight: var(--nxt-font-weight-medium);
}
.nx-metric.tone-info {
  --metric-figure: var(--nxt-info-subtle-fg);
}
.nx-metric.tone-success {
  --metric-figure: var(--nxt-success-subtle-fg);
}
.nx-metric.tone-warning {
  --metric-figure: var(--nxt-warning-subtle-fg);
}
.nx-metric.tone-danger {
  --metric-figure: var(--nxt-danger-subtle-fg);
}
.nx-metric__delta {
  align-self: flex-start;
  display: inline-flex;
  gap: var(--nxt-space-2xs);
  padding: 0 var(--nxt-space-xs);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-neutral-subtle-bg);
  color: var(--nxt-neutral-subtle-fg);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: 1.6;
}
.nx-metric__delta.is-good {
  background: var(--nxt-success-subtle-bg);
  color: var(--nxt-success-subtle-fg);
}
.nx-metric__delta.is-bad {
  background: var(--nxt-danger-subtle-bg);
  color: var(--nxt-danger-subtle-fg);
}
.nx-metric__hint {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
}
.nx-metric__error {
  color: var(--nxt-danger-subtle-fg);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
}
</style>
